import request from 'superagent';

const cache = {};

/**
 * Parse the URL args to get the API url.
 *
 * @since  5.15.25
 *
 * @param  {string|Object} args        Arguments for the request. If string, then the url itself,
 *                                     if object then more options can be passed
 * @param  {string}        args.url    The full url to request.
 *
 * @returns {string}      URL
 */
const parseUrl = (args) => {
	if ('object' !== typeof args) {
		return args;
	}

	if (args.url) {
		return args.url;
	}

	return '';
};

/**
 * Parse the given args into a usable set of arguments for the request.
 *
 * @since  5.15.25
 *
 * @param  {string}        method      The request method.
 * @param  {string|Object} args        Arguments for the request. If string, then the url itself,
 *                                     if object then more options can be passed
 * @param  {string}        args.method The request method. GET, POST, PUT, PATCH, DELETE.
 * @param  {string}        args.url    The full url to request.
 * @param  {string}        args.cached If this request should or not be cached. Default true for GET, false for otherss
 *
 * @returns {Object}      Arguments for the request.
 */
const parseArgs = (method, args) => {
	const url = parseUrl(args);
	if ('object' !== typeof args) {
		args = {};
	}
	args.url = url;
	args.method = method;

	const defaults = {
		cached: 'get' === method,
	};
	args = { ...defaults, ...args };

	return args;
};

/**
 * Performs a request to the OM api.
 *
 * @since  5.15.25
 *
 * @param  {string}        method      The request method.
 * @param  {string|Object} args        Arguments for the request. If string, then the url itself,
 *                                     if object then more options can be passed
 * @param  {string}        args.method The request method. GET, POST, PUT, PATCH, DELETE.
 * @param  {string}        args.url    The full url to request.
 * @param  {string}        args.cached If this request should or not be cached. Default true for GET, false for others
 *
 * @returns {Promise}       Returns the request promise.
 */
const makeRequest = (method = 'get', args) => {
	args = parseArgs(method, args);
	const canCache = args.cached;
	const { url } = args;
	const cacheKey = url + method;
	let result = canCache ? cache[cacheKey] : null;

	if (!result || result._aborted) {
		result = request[method](url).on('abort', () => delete cache[cacheKey]);
	}

	// If we can cache, let's cache.
	if (canCache) {
		cache[cacheKey] = result;
	}

	return result;
};

/**
 * Performs a get request to the OM api.
 *
 * @since  5.15.25
 *
 * @param  {string|Object} args        Arguments for the request. If string, then the url itself,
 *                                     if object then more options can be passed
 * @param  {string}        args.method The request method. GET, POST, PUT, PATCH, DELETE.
 * @param  {string}        args.url    The full url to request.
 * @param  {string}        args.cached If this request should or not be cached. Default true for GET, false for others
 *
 * @returns {Promise}     Returns the request promise.
 */
const getRequest = (args) => makeRequest('get', args);

/**
 * Performs a post request to the OM api.
 *
 * @since  5.15.25
 *
 * @param  {string|Object} args        Arguments for the request. If string, then the url itself,
 *                                     if object then more options can be passed
 * @param  {string}        args.method The request method. GET, POST, PUT, PATCH, DELETE.
 * @param  {string}        args.url    The full url to request.
 * @param  {string}        args.cached If this request should or not be cached. Default true for GET, false for others
 *
 * @returns {Promise}     Returns the request promise.
 */
const postRequest = (args) => makeRequest('post', args);

/**
 * Performs a put request to the OM api.
 *
 * @since  5.15.25
 *
 * @param  {string|Object} args        Arguments for the request. If string, then the url itself,
 *                                     if object then more options can be passed
 * @param  {string}        args.method The request method. GET, POST, PUT, PATCH, DELETE.
 * @param  {string}        args.url    The full url to request.
 * @param  {string}        args.cached If this request should or not be cached. Default true for GET, false for others
 *
 * @returns {Promise}     Returns the request promise.
 */
const putRequest = (args) => makeRequest('put', args);

/**
 * Performs a delete request to the OM api.
 *
 * @since  5.15.25
 *
 * @param  {string|Object} args        Arguments for the request. If string, then the url itself,
 *                                     if object then more options can be passed
 * @param  {string}        args.method The request method. GET, POST, PUT, PATCH, DELETE.
 * @param  {string}        args.url    The full url to request.
 * @param  {string}        args.cached If this request should or not be cached. Default true for GET, false for others
 *
 * @returns {Promise}     Returns the request promise.
 */
const deleteRequest = (args) => makeRequest('delete', args);

export default {
	get: getRequest,
	post: postRequest,
	put: putRequest,
	delete: deleteRequest,
	request: makeRequest,
	cache,
	clearCache: (key) => {
		delete cache[key];
	},
};
